import { animate, motion, useInView, useReducedMotion, type Variants } from "framer-motion";
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";

/**
 * `useLayoutEffect` warns when it runs on the server, where it does nothing.
 * Falling back to `useEffect` there keeps the console clean without changing
 * behaviour in the browser, which is the only place either one matters here.
 */
const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;

/**
 * Motion, used sparingly.
 *
 * The brief is explicit: subtle animation only where it improves
 * comprehension. On a financial advisory site, movement that draws attention to
 * itself reads as sales pressure — the opposite of what the design is for.
 *
 * The core effect is one thing: content rises as it enters the viewport,
 * optionally in sequence, so a group reads as a group. No parallax, no card
 * flips, nothing that moves without saying something about the layout.
 *
 * `CountUp` is the one exception, added at the client's request against the
 * original brief. Animating a figure dramatises it, which is why the block that
 * uses it carries a qualifier stating the numbers are not a performance record.
 *
 * Every animation is disabled outright under `prefers-reduced-motion`. Not
 * shortened — removed. For someone with a vestibular disorder a faster
 * animation is not a kinder one.
 */

/**
 * How far an element travels as it arrives.
 *
 * Raised from 16px, because duration alone was not the problem. A 16px rise is
 * below the threshold at which movement is legible in peripheral vision — the
 * eye catches the fade and not the motion, so the animation reads as "it was
 * already there" no matter how long it takes. Distance is what makes an
 * entrance perceptible; duration only controls how long you have to notice it.
 */
const RISE_DISTANCE = 24;

/**
 * Timings, in one place.
 *
 * Originally 0.5s with a 60ms stagger — a row of four cards finished arriving
 * in under a fifth of a second, which the eye registers as "the section
 * appeared" rather than "these are four things, in this order". 0.9s/160ms was
 * still reading as instant, so both are raised again.
 *
 * The stagger is what communicates that a group is a group, and it matters more
 * than the duration: at 280ms a four-card row takes roughly two seconds to
 * complete, which is slow enough to watch each card land. That is the intent
 * here — the animation is meant to explain the layout, not decorate it.
 *
 * This is now close to the ceiling. Past about 1.5s an entrance stops reading
 * as arrival and starts reading as a slow page, and someone scrolling quickly
 * will scroll past content that has not finished appearing.
 *
 * `EASE` is the same curve as `--ease-out-soft` in the design tokens. The two
 * are duplicated rather than shared because Framer Motion needs the control
 * points as numbers and CSS needs them as a string; they must be changed
 * together.
 */
const DURATION = 1.25;
const STAGGER = 0.28;
const EASE = [0.22, 1, 0.36, 1] as const;

/**
 * How far into view an element must be before it starts.
 *
 * Reduced from 80px alongside the longer duration: a slow animation triggered
 * late is still running when its element is already well up the screen, which
 * reads as lag rather than as arrival.
 */
const VIEWPORT = { once: true, margin: "-40px" } as const;

export function Reveal({
  children,
  delay = 0,
  className,
  as = "div",
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
  as?: "div" | "section" | "li" | "article";
}) {
  const prefersReducedMotion = useReducedMotion();
  const Component = motion[as];

  if (prefersReducedMotion) {
    const Static = as;
    return <Static className={className}>{children}</Static>;
  }

  return (
    <Component
      className={className}
      initial={{ opacity: 0, y: RISE_DISTANCE }}
      whileInView={{ opacity: 1, y: 0 }}
      // `once` matters: re-animating on every scroll past is distracting rather
      // than informative, and on a long page it never stops happening.
      viewport={VIEWPORT}
      transition={{ duration: DURATION, delay, ease: EASE }}
    >
      {children}
    </Component>
  );
}

/**
 * Reveals children in sequence.
 *
 * The stagger is short — 60ms — so a list reads as arriving together with a
 * slight cascade, rather than as items queueing for attention.
 *
 * The list watches the viewport itself and drives `animate`, rather than
 * handing the job to `whileInView`. That distinction is the whole reason this
 * component is written the way it is, and it is not a style preference:
 *
 * `whileInView` is a gesture. It hands the `visible` variant down to whichever
 * children exist at the moment it fires, and with `once` it fires exactly one
 * time. Children that mount *later* were not there to receive it, and nothing
 * ever offers it to them again — so they sit at the `hidden` variant they
 * mounted with, which is `opacity: 0`.
 *
 * That is not a hypothetical. Every list here keys its children by record id,
 * so navigating between two service pages, or paging through insights, unmounts
 * the old items and mounts new ones underneath a list element that React keeps
 * in place — the list has already fired, the new items never get told to
 * appear, and the section renders as blank space. It comes back on reload only
 * because a reload remounts the list along with them.
 *
 * `animate` is declarative instead: it describes the state the list is in, so
 * anything that mounts into it inherits that state and animates in on arrival.
 * The stagger still reads the same on first view, which is what it is for.
 */
export function RevealList({
  children,
  className,
  as = "ul",
}: {
  children: ReactNode;
  className?: string;
  as?: "ul" | "ol" | "div";
}) {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, VIEWPORT);

  if (prefersReducedMotion) {
    const Static = as;
    return <Static className={className}>{children}</Static>;
  }

  const container: Variants = {
    hidden: {},
    visible: { transition: { staggerChildren: STAGGER } },
  };

  const Component = motion[as];

  return (
    <Component
      // `motion[as]` is a union of three components, so its ref parameter is a
      // union TypeScript cannot narrow from the `as` string. The element is an
      // HTMLElement whichever branch it takes, which is all `useInView` reads.
      ref={ref as never}
      className={className}
      variants={container}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
    >
      {children}
    </Component>
  );
}

export function RevealItem({
  children,
  className,
  as = "li",
}: {
  children: ReactNode;
  className?: string;
  as?: "li" | "div";
}) {
  const prefersReducedMotion = useReducedMotion();

  if (prefersReducedMotion) {
    const Static = as;
    return <Static className={className}>{children}</Static>;
  }

  const Component = motion[as];

  return (
    <Component
      className={className}
      variants={{
        hidden: { opacity: 0, y: RISE_DISTANCE },
        visible: { opacity: 1, y: 0, transition: { duration: DURATION, ease: EASE } },
      }}
    >
      {children}
    </Component>
  );
}

/**
 * A number that counts up to its final value when scrolled into view.
 *
 * Three things this deliberately does not do:
 *
 * It does not invent the number. The value rendered on the server, and the one
 * a crawler or a reader without JavaScript sees, is the real figure — the
 * animation only replays the approach to it. A component that server-rendered
 * "0" would be publishing a false statistic to anything that does not run
 * scripts.
 *
 * It does not count for a screen reader. The caller hides the animating text
 * and exposes the final value once; a number ticking through two hundred
 * intermediate states is noise to anyone listening rather than looking.
 *
 * It does not touch non-numeric values. An editor is free to type "Fee-based"
 * into a statistic, and that must render as typed rather than as NaN.
 */
export function CountUp({ value, duration = 1.6 }: { value: string; duration?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const prefersReducedMotion = useReducedMotion();

  // Grouping separators are stripped to parse, then restored on output, so
  // "1,200" counts as a number and still reads as one.
  const grouped = value.includes(",");
  const target = Number(value.replace(/[\s,]/g, ""));
  const decimals = value.includes(".") ? (value.split(".")[1]?.length ?? 0) : 0;
  const isNumeric = value.trim() !== "" && Number.isFinite(target);

  const format = (n: number) =>
    grouped
      ? n.toLocaleString("en-KE", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      : n.toFixed(decimals);

  /**
   * Seeded with the final value so the first client render matches the server's
   * and hydration stays quiet. It is only zeroed once we know we are animating.
   */
  const [display, setDisplay] = useState(value);
  const animated = isNumeric && !prefersReducedMotion;

  useIsomorphicLayoutEffect(() => {
    if (!animated || !inView) return;

    // Zeroing and starting in the same layout effect means the jump to zero is
    // never painted — the eye sees the count begin, not the number reset.
    setDisplay(format(0));

    const controls = animate(0, target, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (current) => setDisplay(format(current)),
      onComplete: () => setDisplay(value),
    });

    return () => controls.stop();
  }, [animated, inView, target, duration, value]);

  return <span ref={ref}>{display}</span>;
}
